/**
 * YouTube playback, as an {@link Engine}.
 *
 * YouTube's terms require the embedded player to stay visible while it plays,
 * so the app keeps a small player card on screen rather than hiding the iframe.
 */

import { Emitter } from '../lib/emitter.ts';
import { clamp } from '../lib/math.ts';
import type { Engine, EngineEvents } from './engine.ts';
import type { YTPlayer } from './youtube-api.ts';
import { nearestRate, openSession, type PlayerSession } from './youtube-player.ts';

export interface LoadOptions {
  autoplay?: boolean;
  startSeconds?: number;
}

export class YouTubeEngine extends Emitter<EngineEvents> implements Engine {
  playing = false;
  videoId: string | null = null;
  volume = 0.85;

  #session: PlayerSession | null = null;
  #booting: Promise<PlayerSession> | null = null;
  #duration = 0;
  #pos = 0;
  #pendingPlay = false;

  constructor(readonly mountId = 'yt-mount') {
    super();
  }

  get player(): YTPlayer | null {
    return this.#session?.player ?? null;
  }

  get ready(): boolean {
    return this.#session !== null;
  }

  get position(): number {
    return this.#pos;
  }

  get duration(): number {
    return this.#duration;
  }

  /**
   * No analyser is possible across the iframe boundary, so the level meter
   * follows a gentle synthetic envelope while playing.
   */
  get level(): number {
    if (!this.playing) return 0;
    const t = performance.now() / 1000;
    return 0.42 + 0.2 * Math.sin(t * 5.3) + 0.12 * Math.sin(t * 11.7) + 0.08 * Math.sin(t * 2.1);
  }

  async ensure(): Promise<YTPlayer> {
    if (this.#session) return this.#session.player;
    this.#booting ??= this.#boot();
    try {
      return (await this.#booting).player;
    } catch (err) {
      this.#booting = null; /* allow a retry after a failed load */
      throw err;
    }
  }

  async load(videoId: string, options: LoadOptions = {}): Promise<void> {
    const { autoplay = false, startSeconds = 0 } = options;
    const player = await this.ensure();
    this.videoId = videoId;
    this.#pos = startSeconds;
    this.#duration = 0;
    this.#pendingPlay = autoplay;
    if (autoplay) player.loadVideoById({ videoId, startSeconds });
    else player.cueVideoById({ videoId, startSeconds });
  }

  async play(): Promise<void> {
    const player = await this.ensure();
    if (this.videoId) player.playVideo();
  }

  pause(): void {
    this.player?.pauseVideo();
    this.playing = false;
  }

  seek(sec: number): void {
    const player = this.player;
    if (!player) return;
    const t = clamp(sec, 0, this.#duration || sec);
    player.seekTo(t, true);
    this.#pos = t;
    this.emit('timeupdate', t);
  }

  setVolume(v: number): void {
    this.volume = clamp(v, 0, 1);
    this.player?.setVolume(Math.round(this.volume * 100));
  }

  /** YouTube quantises playback rate; report back what it actually took. */
  setRate(r: number): number {
    const player = this.player;
    if (!player) return 1;
    const rate = nearestRate(r);
    try {
      player.setPlaybackRate(rate);
    } catch {
      /* unsupported rate — the platter still shows the true pitch */
    }
    return rate;
  }

  destroy(): void {
    try {
      this.#session?.close();
    } catch {
      /* already gone */
    }
    this.#session = null;
    this.#booting = null;
  }

  async #boot(): Promise<PlayerSession> {
    const session = await openSession(this.mountId, {
      playing: (on) => {
        this.playing = on;
        this.emit(on ? 'play' : 'pause');
      },
      ended: () => this.emit('ended'),
      buffering: () => this.emit('buffering'),
      duration: (d) => this.#setDuration(d),
      time: (t) => this.#setTime(t),
      cued: (player) => {
        if (!this.#pendingPlay) return;
        this.#pendingPlay = false;
        player.playVideo();
      },
      error: (msg) => this.emit('error', msg),
    });
    this.#session = session;
    session.player.setVolume(Math.round(this.volume * 100));
    return session;
  }

  #setDuration(d: number): void {
    if (!d || Math.abs(d - this.#duration) < 0.5) return;
    this.#duration = d;
    this.emit('durationchange', d);
  }

  #setTime(t: number): void {
    if (Math.abs(t - this.#pos) <= 0.01) return;
    this.#pos = t;
    this.emit('timeupdate', t);
  }
}
